import React, { useState } from 'react';
import { styled } from 'linaria/react';
import { Form } from 'formik';
import { useIntl } from '@jetshop/intl';
import AddToCartFormik from '@jetshop/core/cart/AddToCartFormik';
import { useDynamicPrice } from '@jetshop/core/hooks/useDynamicPrice';
import { useStockStatus } from '@jetshop/ui/Stock/StockStatusContainer';
import { useNotification } from '@jetshop/core/components/Notifications';
import NotifyWhenBack from '../StockStatus/NotifyWhenBack';
import StockStatusIndicator from '../StockStatus/StockStatusIndicator';
import { useProductValidationMessage } from '../useProductValidationMessage';
import { VariantSelector } from '../VariantSelector';
import { theme } from '../../Theme';
import ProductToastWrapper from './ProductToast';
import addToCartMutation from '../../Cart/queries/addToCart.gql';
import CartQuery from '../../Cart/queries/cartQuery.gql';
import { PushPrice } from '../../ui/StyledPrice';
import { FamilyMembers } from '../FamilyMembers';
import { AddToCartQtyButton } from '../../ui/AddToCartQtyButton';
import KlarnaPaymentInfo from '../KlarnaPayementInfo';
import { Paragraph } from '../../ui/text/Paragraph';
import { Above } from '@jetshop/ui/Breakpoints';
import { PopupContent } from '../../ui/PopupContent';
import { DynamicCategoryContentRenderer } from '../../ContentComponents/DynamicContentRenderer';
import InputWithLabel from '../../Forms/Input';
import { RecommendedPrice } from './RecommendedPrice';
import { ReactComponent as Caliper } from '../../../svg/Caliper.svg';

export const AddToCartWrapper = styled('section')`
  .push-style {
    &.price,
    &.new-price {
      font-size: 30px;
      line-height: 35px;
    }

    &.new-price {
      margin-left: 0;
    }

    &.old-price {
      font-size: 12px;
    }
  }
  .red-tilt-wrapper {
    .percentage {
      font-size: 11px;
    }
  }

  .family-members-wrapper {
    margin-bottom: 2rem;
  }

  .stock-and-payment-row {
    width: 100%;
    height: fit-content;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    flex-wrap: wrap;

    > * {
      margin: 6px 0;
    }

    .heading-4 {
      white-space: nowrap;
    }

    .misc-item {
      width: fit-content;
    }
  }
`;

export const SizeGuideWrapper = styled('div')`
  cursor: pointer;
  width: fit-content;
  .paragraph {
    line-height: 100%;
    font-size: 13px;
    font-weight: 600;
  }

  .caliper {
    height: 34px;
    width: auto;
    margin-right: 10px;
    margin-top: 3px;
  }

  .row {
    display: flex;
    align-items: center;
    height: 35px;
    background: ${theme.colors.greybackground};
    padding: 0 1rem;
    border-radius: 4px;
    width: fit-content;
    border: 1px solid ${theme.colors.grey};

    :active {
      background: ${theme.colors.greybackground + 'aa'};
      transform: scale(0.98);
    }
  }
`;

const StyledForm = styled(Form)`
  display: flex;
  width: 100%;
  flex-direction: column;
  margin-bottom: 10px;
  ${theme.below.md} {
    align-items: center;
  }

  .customer-comment-input-wrapper {
    margin: 10px 0 0 0;
    label {
      font-size: 13px;
      line-height: 17px;
      font-weight: 400;
      color: #1e1e1e;
    }
    input {
      &:focus {
        outline: none;
      }
    }
  }
`;

const QuantityAddToCartWrapper = styled('div')`
  margin-top: 16px;
  display: flex;
  width: 100%;
  align-items: flex-end;
`;

function AddToCartForm({
  product,
  variant,
  getMissingOptions,
  variantHandler
}) {
  const price = useDynamicPrice(product, variant);
  const stockStatus = useStockStatus(variant || product);
  const outOfStockNotify = stockStatus?.status === 'notifyWhenBack';
  const { validation: variantValidation, missingOptions } = variantHandler;
  const { hasVariants } = product;
  const { validationMessage } = useProductValidationMessage({
    missingOptions,
    variantValidation,
    hasVariants,
    stockStatus
  });
  const [trigger, dismiss] = useNotification();
  const [showSizeguide, setShowSizeguide] = useState(false);

  const sizeguide = product?.customFields?.find(
    customField => customField?.key === 'sizeguide'
  )?.stringValue;

  const translate = useIntl();
  const sizeGuideString = translate('Size guide');

  const colorVariants =
    product?.customFields?.find(({ key }) => key === 'colorVariants')
      ?.listValues ?? [];

  const onAddToCartInit = ({ mutationId, quantity }) => {
    trigger(
      <ProductToastWrapper
        selectedVariation={variant}
        product={product}
        quantity={quantity}
      />,
      {
        id: mutationId,
        type: 'add-to-cart'
      }
    );
  };
  const onAddToCartError = () => {
    return ({ mutationId, quantity, error }) => {
      dismiss(mutationId);

      trigger(
        <ProductToastWrapper
          selectedVariation={variant}
          product={product}
          quantity={quantity}
          error={error}
        />,
        {
          type: 'add-to-cart'
        }
      );
    };
  };

  return (
    <AddToCartFormik
      onAddToCartInit={onAddToCartInit}
      onAddToCartError={onAddToCartError}
      cartQuery={CartQuery}
      cartMutation={addToCartMutation}
      product={product}
      variant={variant}
      getMissingOptions={getMissingOptions}
    >
      {() => {
        return (
          <AddToCartWrapper>
            <Above breakpoint="lg">
              <>
                <PushPrice {...price} />
                <RecommendedPrice product={product} />
                <div style={{ height: '2rem' }} />
              </>
            </Above>
            {colorVariants?.length > 0 && (
              <FamilyMembers
                articleNumbers={colorVariants}
                title={'Color'}
                current={product}
                customFieldKey={'color'}
                variant={variant}
              />
            )}
            {product.hasVariants && (
              <VariantSelector
                product={product}
                variantHandler={variantHandler}
                showValidation={!!validationMessage}
              />
            )}
            {sizeguide?.length > 0 && (
              <SizeGuideWrapper
                onClick={() => {
                  setShowSizeguide(true);
                }}
              >
                <div className="row">
                  <Caliper className="caliper" />
                  <Paragraph text={sizeGuideString} />
                </div>
              </SizeGuideWrapper>
            )}
            <StyledForm>
              {product.customerComments &&
                product.customerComments.map(comment => (
                  <InputWithLabel
                    wrapperClassName="customer-comment-input-wrapper"
                    className="customer-comment-input"
                    data-testid={`${comment.name}-input`}
                    id={`comments['${comment.name}']`}
                    name={`comments['${comment.name}']`}
                    label={comment.name}
                    key={comment.name}
                  />
                ))}
              {!outOfStockNotify && (
                <QuantityAddToCartWrapper>
                  <AddToCartQtyButton
                    outOfStockNotify={outOfStockNotify}
                    product={product}
                    variant={variant}
                    productPage={true}
                    validationMessage={validationMessage}
                  />
                </QuantityAddToCartWrapper>
              )}
            </StyledForm>
            <div className="stock-and-payment-row">
              <StockStatusIndicator
                status={stockStatus.status}
                text={stockStatus.text}
              />
              <KlarnaPaymentInfo product={product} />
            </div>
            {outOfStockNotify && (
              <NotifyWhenBack
                articleNumber={(variant || product).articleNumber}
              />
            )}
            {showSizeguide && (
              <Above breakpoint="lg">
                {matches => (
                  <PopupContent
                    type={matches ? null : 'curtain'}
                    open={showSizeguide}
                    setOpen={setShowSizeguide}
                  >
                    <DynamicCategoryContentRenderer
                      categoryId={sizeguide ?? 284} // TODO - remove default sizeguide id
                    />
                  </PopupContent>
                )}
              </Above>
            )}
          </AddToCartWrapper>
        );
      }}
    </AddToCartFormik>
  );
}

export default AddToCartForm;
